// Duplicate detection for ship submissions.
// Used by the submit form (instant warning, against the ships already loaded in the browser) AND by
// functions/api/pending.js (server-side check, so the warning can't be skipped by calling the API directly).
// Plain ES module with no dependencies besides geo.js, so both sides can import it.

import { haversineM } from "./geo.js";

export const LIKELY_RADIUS_M = 25000; // same name within 25 km  -> almost certainly the same wreck
export const NEARBY_RADIUS_M = 1000;  // different name within 1 km -> worth a second look
export const NAME_MATCH = 0.85;       // 0..1 similarity needed to call two names "the same"
const MAX_RESULTS = 5;

const PREFIXES = new Set([
  "ss", "ms", "mv", "mt", "rms", "hms", "uss", "usns", "uscgc", "hmas", "hmcs", "hmnzs", "hnlms", "sv", "ts", "tss",
  "fv", "mfv", "sy", "my", "mf", "sms", "kms", "rv", "tv", "ps", "hmt", "hmy", "ij", "ns", "mn",
]);
const STOP = new Set(["the", "wreck", "of"]);
const ROMAN = /^(i{1,3}|iv|v|vi{1,3}|ix|x)$/;

// "S.S. Édmund Fitzgerald" -> { key: "edmund fitzgerald", tokens: [...], nums: [...] }
export function normalizeTitle(title) {
  let s = String(title || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['’`]/g, "")
    .replace(/\b([a-z])[./]([a-z])\b/g, "$1$2")   // "m/v" "s.s" -> "mv" "ss"
    .replace(/\b([a-z])\.(?=[a-z]\b)/g, "$1");
  let tokens = s.split(/[^a-z0-9]+/).filter(Boolean);
  // drop a leading "SS" / "HMS" / "MV"… but never reduce the name to nothing
  while (tokens.length > 1 && PREFIXES.has(tokens[0])) tokens.shift();
  const meaningful = tokens.filter((t) => !STOP.has(t)); // "Wreck of the Edmund Fitzgerald"
  if (meaningful.length) tokens = meaningful;
  // "Lusitania II" is not "Lusitania", "U-47" is not "U-48" — but a year like (1958) is just a label
  const isYear = (t) => /^\d{4}$/.test(t) && +t >= 1400 && +t <= 2100;
  const nums = tokens.filter((t) => (/^\d+$/.test(t) && !isYear(t)) || ROMAN.test(t)).sort();
  tokens = tokens.filter((t) => !isYear(t) || tokens.length === 1);
  return { key: tokens.join(" "), tokens, nums };
}

function levenshtein(a, b) {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

// Accepts raw titles or already-normalised ones (pass normalizeTitle() results to avoid redoing the work).
export function titleSimilarity(a, b) {
  const A = typeof a === "string" ? normalizeTitle(a) : a;
  const B = typeof b === "string" ? normalizeTitle(b) : b;
  if (!A.key || !B.key) return 0;
  if (A.nums.join() !== B.nums.join()) return 0;
  if (A.key === B.key) return 1;

  // "Edmund Fitzgerald" inside "SS Edmund Fitzgerald (1958)" — but short names like "Rose" match too much
  const [short, long] = A.key.length <= B.key.length ? [A, B] : [B, A];
  if (short.key.length >= 5 && short.tokens.every((t) => long.tokens.includes(t))) return 0.9;

  // typos: "Edmond Fitzgerald"
  const longest = Math.max(A.key.length, B.key.length);
  if (Math.abs(A.key.length - B.key.length) / longest > 1 - NAME_MATCH) return 0; // can't reach the threshold
  return 1 - levenshtein(A.key, B.key) / longest;
}

const num = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));
const fmtDist = (m) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(m < 10000 ? 1 : 0)} km`);

// ships:  [{ id, title, lat, lng, imo?, status? }]   (status defaults to "approved")
// query:  { title, lat?, lng?, imo? }
// Returns up to 5 matches, best first:
//   { id, title, lat, lng, status, level: "likely" | "possible", distanceM, reasons: [..] }
// "likely" matches are the ones the form asks the person to confirm before submitting.
export function findSimilar(ships, { title, lat, lng, imo } = {}) {
  const q = normalizeTitle(title);
  const qLat = num(lat), qLng = num(lng);
  const hasPos = Number.isFinite(qLat) && Number.isFinite(qLng);
  const qImo = parseInt(imo, 10) || 0;
  const hasName = q.key.length >= 3;
  if (!hasName && !hasPos && !qImo) return [];

  const out = [];
  for (const s of ships || []) {
    const sLat = num(s.lat), sLng = num(s.lng);
    const dist = hasPos && Number.isFinite(sLat) && Number.isFinite(sLng) ? haversineM([qLat, qLng], [sLat, sLng]) : null;
    const sim = hasName ? titleSimilarity(q, s.title) : 0;
    const sameName = sim >= NAME_MATCH;
    const sameImo = qImo > 0 && (parseInt(s.imo, 10) || 0) === qImo;

    const reasons = [];
    let level = null;
    if (sameImo) { level = "likely"; reasons.push("Same IMO number"); }
    if (sameName) {
      reasons.push(sim === 1 ? "Same name" : "Very similar name");
      if (dist !== null && dist <= LIKELY_RADIUS_M) level = "likely";
      else if (!level) level = "possible"; // same name but far away (or no location yet): ships do share names
    }
    if (dist !== null && dist <= NEARBY_RADIUS_M) { level = level || "possible"; reasons.push("Very close by"); }
    if (!level) continue;
    if (dist !== null) reasons.push(`${fmtDist(dist)} away`);

    out.push({ id: s.id, title: s.title, lat: sLat, lng: sLng, status: s.status || "approved", level, distanceM: dist, sim, reasons });
  }

  out.sort((a, b) =>
    (a.level === b.level ? 0 : a.level === "likely" ? -1 : 1) ||
    b.sim - a.sim ||
    (a.distanceM ?? Infinity) - (b.distanceM ?? Infinity)
  );
  return out.slice(0, MAX_RESULTS).map(({ sim, ...rest }) => rest);
}
